"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
type Stat = {
  value: number;
  suffix: string;
  prefix: string;
  isDegree: boolean;
  label: string;
  duration: number;
};
const stats: Stat[] = [{
  value: 25,
  suffix: "+",
  prefix: "",
  isDegree: false,
  label: "Anni di esperienza clinica",
  duration: 1200
}, {
  value: 5000,
  suffix: "+",
  prefix: "",
  isDegree: false,
  label: "Interventi chirurgici eseguiti",
  duration: 900
}, {
  value: 120,
  suffix: "",
  prefix: "",
  isDegree: false,
  label: "Pubblicazioni scientifiche",
  duration: 1000
}, {
  value: 100,
  suffix: "%",
  prefix: "",
  isDegree: false,
  label: "Dedizione alla cura del paziente",
  duration: 1100
}];
function formatNumber(n: number, originalValue: number): string {
  if (originalValue >= 1000) {
    return Math.floor(n).toLocaleString("it-IT");
  }
  return Math.floor(n).toString();
}
function CountUpNumber({
  target,
  duration,
  start
}: {
  target: number;
  duration: number;
  start: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * progress;
      setCurrent(eased * target);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCurrent(target);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target, duration]);
  return <>{formatNumber(current, target)}</>;
}
export default function Statistics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="4219" id="numeri-e-risultati" className="bg-background text-foreground py-24 md:py-[120px] px-4 md:px-8 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 md:divide-x divide-y md:divide-y-0 divide-border">
          {stats.map((stat, i) => <div data-index={i} key={i} className="group relative px-6 py-10 md:py-6 md:px-8 first:md:pl-0 last:md:pr-0 transition-transform duration-300 hover:scale-[1.02]">
              <div className="flex items-baseline">
                <h3 className="font-default font-bold leading-none tracking-tight text-[5rem] sm:text-[7rem] md:text-[8rem] lg:text-[9rem] text-foreground">
                  {stat.prefix}
                  <CountUpNumber target={stat.value} duration={stat.duration} start={isVisible} />
                </h3>
                {stat.isDegree && <span className="font-default font-bold text-[2rem] md:text-[2rem] leading-none ml-1" style={{
              color: "var(--primary)"
            }}>
                    °
                  </span>}
                {stat.suffix && !stat.isDegree && <span className="font-default font-bold text-[2rem] md:text-[2rem] leading-none ml-1 text-foreground">
                    {stat.suffix}
                  </span>}
              </div>

              <div className="mt-6 mb-4 relative h-px w-12 group-hover:w-24 transition-all duration-500 ease-out bg-primary" />

              <p className="font-default text-base md:text-lg text-muted-foreground leading-snug max-w-xs">
                {stat.label}
              </p>
            </div>)}
        </div>

        <div className="mt-20 md:mt-28 flex flex-col items-center text-center">
          <Separator className="w-16 mb-8 bg-primary h-px" />
          <blockquote className="font-default italic text-xl md:text-2xl text-foreground max-w-3xl leading-relaxed">
            "La medicina è scienza e umanità; ogni percorso clinico è una storia che merita la massima precisione e ascolto."
          </blockquote>
          <cite className="mt-6 not-italic font-default text-xs uppercase tracking-widest text-muted-foreground">
            — Dott. Mirko Colloca, Medico Chirurgo
          </cite>
        </div>
      </div>
    </section>;
}